/**
 * Window-scoped storage management.
 * Each window/tab gets a unique ID that persists for the session.
 * This prevents auth state from leaking across multiple instances running on different ports.
 */

const WINDOW_ID_KEY = '__WINDOW_SESSION_ID__'
const WINDOW_ID_STORAGE_KEY = 'window_session_id'

/**
 * Get or create a unique window ID for this session.
 * Stored in sessionStorage so it's window-specific and persists across refresh.
 */
export function getWindowSessionId(): string {
    if (typeof window === 'undefined') return ''
    
    try {
        let windowId = sessionStorage.getItem(WINDOW_ID_STORAGE_KEY)
        if (!windowId) {
            // Generate a unique ID for this window session
            windowId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            sessionStorage.setItem(WINDOW_ID_STORAGE_KEY, windowId)
        }
        return windowId
    } catch (e) {
        // Fallback if sessionStorage is unavailable
        return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }
}

/**
 * Get scoped storage key for window-specific data
 */
export function getScopedStorageKey(baseKey: string): string {
    const windowId = getWindowSessionId()
    return `${baseKey}_${windowId}`
}

/**
 * Set item in window-scoped localStorage
 */
export function setWindowLocaleStorage(key: string, value: string): void {
    if (typeof window === 'undefined') return
    try {
        const scopedKey = getScopedStorageKey(key)
        localStorage.setItem(scopedKey, value)
    } catch (e) {
        console.warn(`Failed to set window-scoped localStorage for ${key}:`, e)
    }
}

/**
 * Get item from window-scoped localStorage
 */
export function getWindowLocaleStorage(key: string): string | null {
    if (typeof window === 'undefined') return null
    try {
        const scopedKey = getScopedStorageKey(key)
        return localStorage.getItem(scopedKey)
    } catch (e) {
        console.warn(`Failed to get window-scoped localStorage for ${key}:`, e)
        return null
    }
}

/**
 * Remove item from window-scoped localStorage
 */
export function removeWindowLocaleStorage(key: string): void {
    if (typeof window === 'undefined') return
    try {
        const scopedKey = getScopedStorageKey(key)
        localStorage.removeItem(scopedKey)
    } catch (e) {
        console.warn(`Failed to remove window-scoped localStorage for ${key}:`, e)
    }
}
